import { useState, useEffect } from "react";
import {
  discountSchemeService,
  DiscountScheme,
} from "../../../services/discount-scheme.service";
import type { Product } from "../../../services/product.service";

export function useDiscountEngine() {
  const [activeSchemes, setActiveSchemes] = useState<DiscountScheme[]>([]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const schemes = await discountSchemeService.getActive();
        setActiveSchemes(schemes || []);
      } catch {
        setActiveSchemes([]);
      }
    };
    loadSchemes();
  }, []);

  const calculateDiscountAmount = (
    lineTotal: number,
    discountType: "AMOUNT" | "PERCENT",
    discountValue: number,
  ) => {
    const amount =
      discountType === "PERCENT"
        ? (lineTotal * discountValue) / 100
        : discountValue;
    return Math.max(0, Math.min(amount, lineTotal));
  };

  const calculateSchemeImpactForScheme = (
    scheme: DiscountScheme,
    product: Product,
    lineTotal: number,
    quantity: number,
  ): { discountAmount: number; freeQuantity: number } => {
    if (!scheme.isActive) {
      return { discountAmount: 0, freeQuantity: 0 };
    }
    const appliesToAll = scheme.appliesTo === "ALL" || !scheme.appliesTo;
    const appliesToProduct =
      scheme.appliesTo === "PRODUCT" && scheme.productId === product.id;
    const appliesToCategory =
      scheme.appliesTo === "CATEGORY" &&
      scheme.categoryId === product.categoryId;

    if (!appliesToAll && !appliesToProduct && !appliesToCategory) {
      return { discountAmount: 0, freeQuantity: 0 };
    }

    let discountAmount = 0;
    let freeQuantity = 0;

    switch (scheme.discountType) {
      case "FLAT":
        discountAmount = Number(
          scheme.discountAmount || scheme.discountValue || 0,
        );
        break;
      case "PERCENTAGE":
        discountAmount =
          (lineTotal *
            Number(scheme.discountPercent || scheme.discountValue || 0)) /
          100;
        break;
      case "TIERED":
        if (lineTotal >= 10000) {
          discountAmount = (lineTotal * 10) / 100;
        } else if (lineTotal >= 5000) {
          discountAmount = (lineTotal * 5) / 100;
        } else if (lineTotal >= 2000) {
          discountAmount = (lineTotal * 2) / 100;
        }
        break;
      case "VOLUME":
        if (scheme.minQuantity && quantity >= scheme.minQuantity) {
          discountAmount =
            (lineTotal * Number(scheme.discountPercent || 0)) / 100;
        }
        break;
      case "BOGO":
        if (scheme.buyQuantity && scheme.getQuantity) {
          const sets = Math.floor(quantity / scheme.buyQuantity);
          freeQuantity = sets * scheme.getQuantity;
        }
        discountAmount = 0;
        break;
      default:
        break;
    }

    if (scheme.maxDiscountAmount) {
      discountAmount = Math.min(
        discountAmount,
        Number(scheme.maxDiscountAmount),
      );
    }

    return {
      discountAmount: Math.max(0, discountAmount),
      freeQuantity,
    };
  };

  const calculateSchemeDiscount = (
    product: Product,
    lineTotal: number,
    quantity: number,
  ) => {
    return activeSchemes.reduce((sum, scheme) => {
      const impact = calculateSchemeImpactForScheme(
        scheme,
        product,
        lineTotal,
        quantity,
      );
      return sum + impact.discountAmount;
    }, 0);
  };

  const calculateSchemeFreeQuantity = (
    product: Product,
    lineTotal: number,
    quantity: number,
  ) => {
    return activeSchemes.reduce((sum, scheme) => {
      const impact = calculateSchemeImpactForScheme(
        scheme,
        product,
        lineTotal,
        quantity,
      );
      return sum + impact.freeQuantity;
    }, 0);
  };

  return {
    activeSchemes,
    calculateDiscountAmount,
    calculateSchemeImpactForScheme,
    calculateSchemeDiscount,
    calculateSchemeFreeQuantity,
  };
}
